import { useState } from "react";
import { Button, Modal, NumberInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { DrawerWarning } from "./DrawerWarning";
import { TenderReferenceField } from "./TenderReferenceField";
import {
  hasPendingTenderReference,
  tenderReferenceField,
} from "@/modules/pos/lib/tenderReference";
import { countedKey } from "@/lib/i18n/plural";
import { useMoney } from "@/shared/ui";
import {
  outstandingOnBill,
  partyShares,
  unclaimedValueOnBill,
  unmarkedPlates,
  type PartyShare,
} from "@/modules/pos/lib/partyShares";
import type { Invoice, SettlementMethod } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * The methods one tender may come in by.
 *
 * INSTAPAY is NOT here, and its absence is the rule rather than an omission:
 * `SettleSheet` offers it only when a transfer for this bill has actually been
 * CONFIRMED by staff (D44), because "a permanent third pill would invite
 * closing an unpaid bill as 'transfer' on nothing but the customer's word".
 * This sheet takes money too, so it follows the same rule — it was shipped
 * offering INSTAPAY unconditionally, and unit 4's browser round read it.
 *
 * A transfer is the worst method to get wrong here: it is revenue in NEITHER
 * drawer, so a tender recorded against a transfer nobody checked never shows up
 * as missing at close.
 */
const BASE_METHODS = ["CASH", "CARD"] as const;

/**
 * TAKING PART OF A BILL (B3, B43), at the till.
 *
 * Two cases the founder gave, one command, and the difference between them is
 * the whole design:
 *
 * · **A SPLIT TENDER** — «نص كاش ونص فيزا». The cashier knows the card will
 *   only take 300 and the rest is coming in cash. That is a decision about the
 *   card limit, not about the food, so the CASHIER types the amount.
 * · **A PARTY** — one family paying for the plates it ordered. The till types
 *   NOTHING: the amount is derived server-side from the party's plate set,
 *   because a till that could type what a family owes could type the wrong
 *   number. The figure shown on the chip is quoted from the same allocation the
 *   server will settle with, so the cashier reads it before confirming.
 *
 * SEPARATE FROM `SettleSheet`, deliberately. That sheet is the one-tap
 * whole-bill path the overwhelming majority of bills take, and its validation
 * is the opposite of this one's: it demands AT LEAST the total, this one takes
 * at most what is left. Folding them would put two contradictory rules behind
 * one control on the busiest screen in the product.
 *
 * NO WRITE-OFF HERE, and not by omission: a write-off is the decision that a
 * WHOLE bill will never be paid, and it deliberately writes no ledger row at
 * all. "Write off a third of this bill" is a different act nobody has decided
 * the meaning of; the server refuses it, and offering the button would be a
 * refusal waiting to happen.
 *
 * THE BILL SETTLES ITSELF when the tenders reach the total — SETTLED is
 * derived, never a fifth status value. So this sheet says what the tender being
 * confirmed will DO: finish the bill, or leave a balance and how much.
 */
export function PartialSettleSheet({
  invoice,
  isSubmitting,
  warning,
  onOpenDrawer,
  transferConfirmed = false,
  initialMethod = "CASH",
  onCancel,
  onConfirm,
}: {
  invoice: Invoice;
  isSubmitting: boolean;
  /**
   * The method the cashier already chose on the whole-bill sheet before tapping
   * "take part of it" (B3), or `CASH` when this sheet is opened cold.
   *
   * It is carried because the question was already ANSWERED one tap ago: the
   * cashier picks CARD, discovers the card will only take part of it, taps
   * through — and this sheet reset to CASH underneath them. The tender then
   * went onto the books as cash, so the drawer counts short by the amount at
   * close and the takings split names a tender that never happened. Resetting
   * the answer is not the same caution as `transferConfirmed` or the party
   * picker below, which are deliberately unset because nobody has answered
   * them yet.
   */
  initialMethod?: SettlementMethod;
  /**
   * A diner's bank transfer for this bill has been CONFIRMED by staff (D44) —
   * the same prop and the same rule as `SettleSheet`. Only then is INSTAPAY
   * offered: `SUBMITTED` is still just the customer's word.
   *
   * NOT preselected here, unlike the whole-bill sheet. There the confirmed
   * transfer IS the payment and any other method is a mis-tap; here it may
   * cover one share of several, and which share is being taken now is the
   * cashier's to say.
   */
  transferConfirmed?: boolean;
  /**
   * The D25 pre-warning, taken exactly as the settle sheet takes it: with shift
   * tracking on and no drawer open the server refuses this tender as
   * `RESTAURANT_SHIFT_REQUIRED`. Money is physically arriving at a till, so it
   * has to arrive in a drawer that is open.
   */
  warning?: string;
  /**
   * FIX IT WITHOUT LEAVING THIS SHEET (Unit C) — opens the drawer dialog over it.
   *
   * This sheet used to state the refusal and stop there, which left a cashier
   * holding a customer's money and no move that keeps the sale on the system.
   * Omitted for anyone without the drawer rung: their sentence names a person to
   * ask, and a button whose only outcome is a refusal teaches them to stop reading
   * the warning.
   */
  onOpenDrawer?: () => void;
  onCancel: () => void;
  /**
   * Resolves `{ ok: true }` only when the tender was TAKEN — the shape
   * `PaperSettleSheet`'s `send` reports. Anything else (refused, failed, a
   * conflict) is `{ ok: false }`, and the sheet keeps the cashier's answers for
   * the retry.
   */
  onConfirm: (input: {
    method: SettlementMethod;
    /** Omitted when a party is named — the server derives it. */
    amount?: number;
    partyId?: string;
    /**
     * D163 — this tender's «مرجع العملية», trimmed; present only with
     * CARD/INSTAPAY and only when something was typed.
     */
    reference?: string;
  }) => Promise<{ ok: boolean }>;
}) {
  const t = useTranslate();
  const money = useMoney();

  const outstanding = outstandingOnBill(invoice);
  const parties = partyShares(invoice).filter((share) => share.itemCount > 0);
  /*
   * THE PLATES NOBODY HAS CLAIMED, and what they come to.
   *
   * The split sheet warns about these; this one — the screen where the money
   * actually changes hands — did not, so a cashier could take both families'
   * shares off a three-family table and be left with a balance no chip
   * explains. That is the exact failure the split feature exists to prevent,
   * unguarded at the only moment it happens. Rendered only when there ARE any,
   * for the reason the split sheet gives: a "0 unclaimed" line on every split
   * teaches a cashier to stop reading the one line that matters.
   */
  const unclaimed = unmarkedPlates(invoice);
  const unclaimedValue = unclaimedValueOnBill(invoice);

  /*
   * Carried from the whole-bill sheet, but only when THIS sheet offers it.
   * `SettleSheet` can be showing `AGGREGATOR`, which is not a tender a split
   * is taken in, and seeding the state with a value that has no pill would
   * leave every pill unselected while `method` still confirmed that value.
   */
  const offeredMethods: readonly SettlementMethod[] = transferConfirmed
    ? [...BASE_METHODS, "INSTAPAY"]
    : BASE_METHODS;
  const [method, setMethod] = useState<SettlementMethod>(
    offeredMethods.includes(initialMethod) ? initialMethod : "CASH",
  );
  /**
   * Which party is paying — `null` is "a typed amount", the B3 case.
   *
   * DEF-7: nothing pre-selected among the parties. A pre-filled picker reads as
   * already answered, and on this sheet the stale value would take a family's
   * money for a share nobody asked them for.
   */
  const [partyId, setPartyId] = useState<string | null>(null);
  /* A string, not a number: "15" mid-typing must not snap to NaN or 0. Blank
     rather than pre-filled with the outstanding balance — the amount IS the
     decision here, and pre-filling makes the sheet look answered. The "all of
     it" button below is what makes the common case one tap anyway. */
  const [typed, setTyped] = useState("");
  /* D163 — this tender's «مرجع العملية»; cleared with the other answers, once
     the tender is taken. */
  const [reference, setReference] = useState("");

  const party = parties.find((share) => share.id === partyId) ?? null;
  const typedAmount = Number(typed);
  /*
   * A party tender takes what is LEFT of that family's share, never the whole
   * share — the same subtraction the server makes, so the figure quoted here
   * and the figure taken cannot disagree. Quoting the whole share is what
   * shipped, and with nothing marking a family as paid it let one family be
   * charged three times over in three taps.
   */
  const amount = party ? party.outstanding : typedAmount;

  /*
   * WHAT MAY BE CONFIRMED.
   *
   * A typed amount must be a real, positive number — the server refuses
   * anything else — and CARD and INSTAPAY are additionally capped at what is
   * left: a terminal is charged a figure, and charging one MORE than the bill
   * still owes is money the restaurant has to hand back through a refund. CASH
   * is not capped, because a customer handing over a 200 note for a 153.90
   * remainder is the ordinary case and the change is shown below.
   *
   * A party's amount is never typed and never validated here: the server
   * derives it, and `null` means only that the till could not state the split.
   */
  const overCharging =
    method !== "CASH" && !party && typedAmount > outstanding;
  const canConfirm =
    !isSubmitting &&
    (party
      ? // A family with nothing left to pay is refused HERE as well as at the
        // server (`RESTAURANT_PARTY_ALREADY_SETTLED`), so a cashier never meets
        // the refusal: the chip below is disabled and says "Paid" instead.
        // `null` — the till cannot state the split — is still allowed through,
        // because the server is the authority on the figure either way.
        party.outstanding === null || party.outstanding > 0
      : Number.isFinite(typedAmount) && typedAmount > 0 && !overCharging);

  const change =
    method === "CASH" && !party && Number.isFinite(typedAmount)
      ? Math.max(0, Number((typedAmount - outstanding).toFixed(2)))
      : 0;
  // What this tender LEAVES. Change is not part of it: a 200 note against a
  // 153.90 balance settles the bill and 46.10 goes back across the counter.
  const remaining = Number.isFinite(amount ?? NaN)
    ? Math.max(0, Number((outstanding - (amount ?? 0)).toFixed(2)))
    : null;

  // A typed tender, or a family picked to pay. Both are answers the cashier
  // gave with a customer at the counter.
  const guard = useDiscardGuard(
    typed !== "" ||
      partyId !== null ||
      hasPendingTenderReference(method, reference),
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={t((m) => m.pos.partialTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="partial-settle-sheet"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={guard.requestClose}
            data-testid="partial-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            /* PRIMARY, explicitly. Reading the round's screenshots found this
               button rendering as bare text beside a boxed Cancel — so the
               action that takes the money had less visual weight than the one
               that abandons it, at both widths and in both locales. The
               settle sheet beside it has always said `variant="primary"`.

               `disabled`, NOT `aria-disabled`: UX-round D23 names exactly two call
               «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
               sites for that change, and this is not one of them —
               `PartialSettleSheet.spec.tsx:119` pins this button's disabled
               state, and it is pinned because an over-charge is refused by the
               SERVER too. */
            variant="primary"
            size="touch"
            className="font-extrabold"
            disabled={!canConfirm}
            isLoading={isSubmitting}
            data-testid="partial-confirm"
            onClick={() => {
              if (!canConfirm) return;
              // A party tender carries NO amount — not the quoted figure, not a
              // rounded copy of it. The server derives it, and a number sent
              // "for convenience" is a number that can disagree.
              // D163 — `reference` only when this method takes one and
              // something was typed: the key is absent otherwise.
              void onConfirm(
                party
                  ? {
                      method,
                      partyId: party.id,
                      ...tenderReferenceField(method, reference),
                    }
                  : {
                      method,
                      amount: typedAmount,
                      ...tenderReferenceField(method, reference),
                    },
              ).then((outcome) => {
                /*
                 * THE ANSWERS GO, THE SHEET STAYS — `PartySheet`'s rule, for
                 * the same reason and one more. ONLY ONCE THE TENDER IS TAKEN:
                 * the same rule `PaperSettleSheet` keeps (it clears on a
                 * `done` outcome and nothing else).
                 *
                 * The parent keeps this sheet open while a balance remains,
                 * because the next tender is the very next thing that happens.
                 * What it re-renders against is the bill's NEW outstanding — so
                 * a box still reading 50 beside an outstanding of 215.57 is a
                 * figure about a tender that has already been taken, one tap
                 * away from taking it a second time.
                 *
                 * And it is what makes the dismissal guard honest: work that has
                 * been COMMITTED is not unsaved work, and a sheet that asked
                 * "discard?" about money already in the drawer would be a sheet
                 * nobody can leave.
                 *
                 * A refused or failed tender was NOT taken, so nothing here
                 * clears: the amount, the family and the D163 reference stay
                 * for the retry. Clearing them on the tap used to send the
                 * retry without the card's reference — silently — and as a
                 * different command, since the id is reused only for an
                 * unchanged payload (`createRecoverableCommandSender`).
                 */
                if (!outcome.ok) return;
                setTyped("");
                setPartyId(null);
                setReference("");
              });
            }}
          >
            {t((m) => m.pos.partialConfirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* WHERE THE BILL STANDS. The outstanding balance is the big figure
            because it is the one the cashier is acting on — the total and what
            has already come in are the context that makes it believable. Both
            of the smaller lines only appear once money is actually in: on the
            first tender of a split they would be "0.00 paid" and noise. */}
        <div className="flex flex-col gap-0.5 tabular-nums">
          <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
            <span>{t((m) => m.pos.running)}</span>
            <span>{money(invoice.total)}</span>
          </div>
          {invoice.tenderedTotal > 0 ? (
            <div
              className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]"
              data-testid="partial-paid-so-far"
            >
              <span>{t((m) => m.pos.partialPaidSoFar)}</span>
              <span>{money(invoice.tenderedTotal)}</span>
            </div>
          ) : null}
          <div className="mt-[3px] flex items-baseline justify-between border-t border-dashed border-[color:var(--border-strong)] pt-1.5">
            <span className="text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold">
              {t((m) => m.pos.partialOutstanding)}
            </span>
            <span
              className="text-[length:var(--font-size-xl)] font-extrabold tabular-nums"
              data-testid="partial-outstanding"
            >
              {money(outstanding)}
            </span>
          </div>
        </div>

        {warning ? (
          <DrawerWarning
            message={warning}
            actionLabel={t((m) => m.pos.noShiftWarningOpen)}
            onOpenDrawer={onOpenDrawer}
            testId="partial-shift-warning"
          />
        ) : null}

        {/* WHO IS PAYING. Only when the bill has been split — an unsplit bill
            has exactly one answer and a picker with one option is a question
            nobody needs asked. */}
        {parties.length > 0 ? (
          <div>
            <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.pos.partialWhoIsPaying)}
            </span>
            <div className="flex flex-wrap gap-1.5">
              <PayerChip
                current={partyId === null}
                disabled={isSubmitting}
                testId="partial-payer-amount"
                onClick={() => setPartyId(null)}
              >
                {t((m) => m.pos.partialTypedAmount)}
              </PayerChip>
              {parties.map((share) => (
                <PayerChip
                  key={share.id}
                  current={partyId === share.id}
                  /* A family that is square cannot be picked. It STAYS on the
                     screen, marked "Paid": a chip that vanished when a family
                     paid would read as the family having been deleted, and the
                     cashier working down a table of three needs to see which
                     two are done. */
                  disabled={isSubmitting || share.isSettled}
                  testId={`partial-payer-${share.id}`}
                  onClick={() => setPartyId(share.id)}
                >
                  <span className="flex items-center gap-1">
                    {/* ISOLATED name, the chip's own alignment — an Arabic
                        family name beside a Latin one must not drag its figure
                        to the other end. */}
                    <bdi>{share.label}</bdi>
                    <span className="tabular-nums opacity-80">
                      {share.isSettled
                        ? t((m) => m.pos.partialPartyPaid)
                        : share.outstanding === null
                          ? "—"
                          : money(share.outstanding)}
                    </span>
                  </span>
                </PayerChip>
              ))}
            </div>
            {party ? (
              /* WHY THERE IS NO FIELD. Without this the derived amount reads as
                 a till that will not let the cashier type, rather than as the
                 guarantee it is.

                 On a family that has already put SOME of its share in, the
                 arithmetic is said instead: "paid 100 of 233.65" is what makes
                 the smaller figure on the chip believable, and a cashier who
                 cannot see why a family is being asked for less than its plates
                 come to is a cashier who will re-take the difference. */
              <p
                className="mt-1.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
                data-testid="partial-party-note"
              >
                {party.paid > 0 && party.amount !== null
                  ? t((m) => m.pos.partialPartyPaidOf, {
                      paid: money(party.paid),
                      amount: money(party.amount),
                    })
                  : t((m) => m.pos.partialPartyNote)}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* THE PLATES NOBODY HAS CLAIMED — on the screen that takes the money,
            and not only on the one that marks the plates. A table of three
            families whose shares come to less than the bill is the case this
            catches, and the figure is what makes it actionable: the cashier
            can see the gap before the last family walks. */}
        {unclaimed.length > 0 && parties.length > 0 ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] border border-[color:var(--warning)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--warning)]"
            data-testid="partial-unclaimed"
          >
            {t(
              countedKey(unclaimed.length, {
                one: (m) => m.pos.partialUnclaimedOne,
                two: (m) => m.pos.partialUnclaimedTwo,
                few: (m) => m.pos.partialUnclaimedFew,
                many: (m) => m.pos.partialUnclaimed,
              }),
              {
                count: unclaimed.length,
                amount:
                  unclaimedValue === null ? "—" : money(unclaimedValue),
              },
            )}
          </p>
        ) : null}

        <div className="flex gap-2">
          {offeredMethods.map((option) => (
            <Button
              key={option}
              variant={method === option ? "primary" : "outline"}
              size="touch"
              className="flex-1 font-extrabold"
              aria-pressed={method === option}
              data-testid={`partial-method-${option}`}
              onClick={() => setMethod(option)}
            >
              {option === "CASH"
                ? t((m) => m.pos.cash)
                : option === "CARD"
                  ? t((m) => m.pos.card)
                  : t((m) => m.pos.instapay)}
            </Button>
          ))}
        </div>

        {/* D163 — this tender's optional reference, CARD/INSTAPAY only;
            each tender of a split carries its own. Never refuses confirm. */}
        <TenderReferenceField
          method={method}
          value={reference}
          onChange={setReference}
          disabled={isSubmitting}
          testId="partial-reference"
        />

        {party ? null : (
          <label className="flex flex-col gap-1">
            <span className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-bold text-[color:var(--text-muted)]">
                {t((m) => m.pos.partialAmountLabel)}
              </span>
              {/* The common last tender, in one tap: whatever is left. Not a
                  pre-filled field — a number already in the box reads as a
                  decision somebody made, and on the FIRST tender of a split
                  that decision is exactly the wrong one. */}
              <button
                type="button"
                disabled={isSubmitting}
                data-testid="partial-amount-all"
                onClick={() => setTyped(String(outstanding))}
                className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--info)] transition-colors hover:text-[color:var(--text)] disabled:opacity-40"
              >
                {t((m) => m.pos.partialTakeTheRest)}
              </button>
            </span>
            <NumberInput
              name="partialAmount"
              allowDecimal
              min={0}
              dir="ltr"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              hasError={overCharging}
              data-testid="partial-amount"
              className="text-lg font-extrabold tabular-nums"
            />
            {overCharging ? (
              <span className="text-xs font-semibold text-[color:var(--danger)]">
                {t((m) => m.pos.partialOverChargeHint)}
              </span>
            ) : null}
          </label>
        )}

        {change > 0 ? (
          <div
            className="flex items-baseline justify-between rounded-[var(--radius-list)] bg-[color:var(--warning-100,var(--surface-alt))] px-3 py-2"
            data-testid="partial-change"
          >
            <span className="text-sm font-bold">
              {t((m) => m.pos.changeDue)}
            </span>
            <span className="text-lg font-extrabold tabular-nums text-[color:var(--warning)]">
              {money(change)}
            </span>
          </div>
        ) : null}

        {/* WHAT THIS TENDER DOES. The bill settles itself when the tenders
            reach the total, so the sheet says which of the two this is — a
            cashier who cannot predict whether the bill is about to close is a
            cashier who will take the next family's money twice. */}
        {canConfirm && remaining !== null ? (
          <p
            className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--text-muted)]"
            data-testid="partial-effect"
          >
            {remaining === 0
              ? t((m) => m.pos.partialFinishes)
              : t((m) => m.pos.partialLeaves, { amount: money(remaining) })}
          </p>
        ) : null}

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="partial-discard-prompt"
    />
    </>
  );
}

function PayerChip({
  current,
  disabled,
  testId,
  onClick,
  children,
}: {
  current: boolean;
  disabled: boolean;
  testId: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={current}
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
      className={[
        "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
        current
          ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
          : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/** Re-exported so the page can gate the control on the same rule. */
export type { PartyShare };
